<template>
  <div
    v-if="!isFullscreen"
    :data-role="fixed ? 'fixed-toolbar' : 'inline-toolbar'"
    :data-transparent-toolbar="transparent"
    class="v-toolbar__wrapper"
  >
    <div
      :class="[
        {
          '--transparent': transparent,
          '--dark': isDarkBackground,
          '--flat': flat,
          'p-fixed top-0 left-0': fixed && !inline,
          '--inline': inline,
          '--mobile-viewport': isBuilderContext() && this.isMobile
        },
        'v-toolbar'
      ]"
      id="toolbar"
      :style="
        useAccountStyles
          ? { backgroundColor: toolbar.background_color, color: textColor }
          : {}
      "
    >
      <div class="v-toolbar__content">
        <div class="v-toolbar__left">
          <v-button
            v-if="back"
            class="v-toolbar__button"
            round
            ghost
            :style="useAccountStyles ? { color: textColor } : {}"
            data-cy="back"
            data-role="back"
            @click="attemptBack"
          >
            <v-icon icon="arrow_back" small rtl-flip />
          </v-button>

          <v-button
            v-if="withMenu && !isDesktop"
            round
            ghost
            class="v-toolbar__button"
            :style="useAccountStyles ? { color: textColor } : {}"
            data-cy="menu"
            @click="toggleSidebar"
          >
            <v-icon icon="menu" small />
          </v-button>

          <slot name="left" />

          <template v-if="showLeftLogo">
            <v-link class="d-flex align-items-center" to="/">
              <toolbar-logo
                v-if="logo"
                :logo="toolbar.logo"
                :fallback-text="app.name"
                :styles="styles"
              />
            </v-link>
          </template>
        </div>

        <div class="v-toolbar__center">
          <horizontal-toolbar-navigation
            v-if="withNavigation && isDesktop && !transparent && !isTabbarShown"
          />

          <template v-if="showCenteredLogo">
            <v-link class="d-flex align-items-center" to="/">
              <toolbar-logo
                v-if="logo"
                :logo="toolbar.logo"
                :fallback-text="app.name"
                :styles="styles"
              />
            </v-link>
          </template>

          <slot name="center" />
        </div>

        <div class="v-toolbar__right">
          <div id="v-toolbar-right-portal"></div>
          <slot name="right" :text-color="textColor" />

          <v-button
            v-if="withMenu && isDesktop"
            round
            ghost
            @click="toggleSidebar"
            class="v-toolbar__button"
          >
            <v-icon icon="menu" />
          </v-button>
        </div>
      </div>

      <slot name="append" />
    </div>

    <div
      v-if="fixed && !transparent && !inline"
      class="v-toolbar__spacer"
    ></div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { isBuilderContext } from "@/lib/utils"

import enumValidator from "@/lib/validators/enum-validator"
import determineTextColor, { isDarkTextColor } from "@/lib/determine-text-color"
import { withRatio } from "@shared/block-ratio"

import ToolbarLogo, { DEFAULT_STYLES } from "@/components/toolbar/Logo"
import HorizontalToolbarNavigation from "@/components/horizontal-toolbar/Navigation"

export default {
  components: {
    ToolbarLogo,
    HorizontalToolbarNavigation
  },

  props: {
    transparent: { type: Boolean, default: false },
    logo: { type: Boolean, default: true },
    fixed: { type: Boolean, default: false },
    back: { type: Boolean, default: true },
    align: {
      type: String,
      default: null,
      validator: enumValidator(["left", "center"])
    },
    emitOnExitAttempt: { type: Boolean, default: false },
    flat: { type: Boolean, default: false },
    useAccountStyles: { type: Boolean, default: false },
    withMenu: { type: Boolean, default: false },
    pageId: { type: [Number, String], default: null },
    withNavigation: { type: Boolean, default: false },
    inline: { type: Boolean, default: false }
  },

  computed: {
    ...mapState(["global"]),
    ...mapState("videos", ["isFullscreen"]),
    ...mapState("responsive", ["isDesktop", "isMobile"]),
    ...mapGetters("tabbar", ["isTabbarShown"]),

    app() {
      return this.global?.app || {}
    },

    isDarkBackground() {
      return (
        this.useAccountStyles &&
        !isDarkTextColor(this.toolbar?.background_color)
      )
    },

    toolbar() {
      return this.global?.toolbar || {}
    },

    textColor() {
      return determineTextColor(this.toolbar.background_color, {
        black: "#384250",
        white: "#ffffff"
      })
    },

    styles() {
      return {
        ...DEFAULT_STYLES,
        height: withRatio(50, this.toolbar.logo_height_ratio, { unit: "%" })
      }
    },

    hasSlotData() {
      return !!this.$slots.left || !!this.$slots.center
    },

    showLeftLogo() {
      return this.isDesktop && !this.transparent && !this.hasSlotData
    },

    showCenteredLogo() {
      return !this.isDesktop && !this.transparent && !this.hasSlotData
    }
  },

  methods: {
    isBuilderContext,

    ...mapMutations("tabbar", ["toggleSidebar"]),

    goBack() {
      this.$router.back()
    },

    attemptBack() {
      if (this.pageId && EventBus.hasListener(EVENTS.PAGE_EXIT_ATTEMPTED)) {
        EventBus.$emit(EVENTS.PAGE_EXIT_ATTEMPTED, {
          pageId: this.pageId
        })
      } else {
        this.goBack()
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

:root {
  --toolbar-height: 3.5rem;

  @media screen and (min-width: $breakpoint--xl) {
    --toolbar-height: 4.5rem;
  }
}

.v-toolbar {
  background-clip: padding-box;
  user-select: none;
  position: relative;
  z-index: $toolbar--z-index;
  color: $color-ui--grey-90;
  font-weight: $font-weight--base;
  width: 100%;
  border: none;
  border-bottom: 1px solid $color-ui--grey-20;
  background-color: rgba($color-rgb--white, 0.86);
  backdrop-filter: blur(6px);
  padding-top: env(safe-area-inset-top);
  transition: background-color 0.25s ease;

  &.--flat {
    box-shadow: none;
  }

  &.--inline {
    position: relative;
    box-shadow: none;
    background-color: transparent;
    backdrop-filter: none;
    padding-top: 0;
    max-width: var(--container-width);
    margin: 0 auto;
    border: none;
  }

  &.--transparent {
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
    border: none;

    .v-toolbar__button {
      @include clickable(0.9);

      background: $color-ui--white;
      position: relative;
      top: $size--1;
      box-shadow: $box-shadow--medium;
      color: $color-ui--grey-70;
      border-radius: 100px;
      width: $size--9;
      height: $size--9;
      padding: 0 $size--4;

      &:last-child {
        margin-right: 0;
      }

      .v-loader {
        --mvt-loader-size: #{$size--6};
      }
    }

    .v-toolbar__left {
      .v-toolbar__button {
        margin-right: $size--2;
      }

      .v-toolbar__button:first-child {
        margin-left: 0;
      }
    }

    .v-toolbar__right {
      .v-toolbar__button {
        background: rgba($color-rgb--black, 0.2);
        color: $color-ui--white;
        box-shadow: none;
        margin-left: $size--2;

        &.--active {
          background: $color-ui--white;
          box-shadow: $box-shadow--medium;
          color: $color-ui--grey-70;
        }
      }
    }

    .v-toolbar__content {
      padding: 0 $size--2;
      max-width: $content--max-width;
    }
  }

  @media screen and (min-width: $breakpoint--xl) {
    &:not(.--inline) {
      top: 0;
      z-index: $toolbar--z-index + 1;
    }

    &:not(.--transparent):not(.--mobile-viewport) {
      .v-toolbar__left {
        padding-right: $size--8;

        .v-toolbar__button:last-child {
          margin-right: $size--5;
        }
      }
    }
  }
}

.--dark-mode {
  .v-toolbar {
    &.--transparent {
      .v-toolbar__button {
        border: 2px solid white;
      }

      .v-toolbar__right {
        .v-toolbar__button {
          border: none;
          color: white;
        }
      }
    }
  }
}

.--with-left-sidebar {
  [data-role="fixed-toolbar"] .v-toolbar {
    margin-left: $tabbar--desktop-width;
    width: calc(100% - #{$tabbar--desktop-width});
  }
}

.v-toolbar__content {
  display: flex;
  justify-content: space-between;
  flex-wrap: nowrap;
  min-height: $toolbar--height;
  white-space: nowrap;
  max-width: calc($content--max-width + 224px);
  margin: 0 auto;
  padding: 0 $size--1;

  & > div {
    min-height: $toolbar--height;
    display: flex;
    align-items: center;
  }

  .v-toolbar__left,
  .v-toolbar__right {
    flex: 0 1 27%;
  }

  .v-toolbar__left {
    justify-content: flex-start;
    max-width: 80%;

    > a {
      height: 100%;
    }
  }

  .v-toolbar__center {
    flex: 1 2 46%;
    align-items: center;
    justify-content: center;
    max-width: 80%;
    min-width: 0;

    > a {
      min-width: 0;
      overflow: hidden;
      height: 100%;
    }
  }

  .v-toolbar__right {
    display: flex;
    justify-content: flex-end;

    --mvt-loader-size: 1.25rem;
  }

  .v-toolbar__button {
    font-size: 1.35rem;
    color: $color-ui--grey-70;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 $size--5;
    height: 100%;
    margin: 0;
    border: none;

    .v-icon {
      transform: scale(1);
    }
  }

  .v-toolbar__text {
    @include overflow-ellipsis;

    font-weight: $font-weight--bold;
    font-size: 1.35rem;
  }

  @media screen and (min-width: $breakpoint--xl) {
    padding: 0 $size--2;
  }
}

.v-toolbar__spacer {
  height: calc(#{$toolbar--height} + env(safe-area-inset-top));
  background: $color-ui--white;
}

.v-toolbar__button {
  @include clickable(0.9);
}

[dir="rtl"] {
  .v-toolbar {
    &:not(.--transparent) {
      .v-toolbar-logo,
      .v-toolbar__text {
        margin-right: $size--5;
        margin-left: 0;
      }
    }
  }
}
</style>
